import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  GetCapacityReportGQL,
  GetCapacityReportQuery,
  SubmitAndCompleteCapacityReportGQL,
  UpdateCapacityReportGQL,
  UpdateCapacityReportInput,
} from '@libs/services/graphql/generated/ala-backend.service';
import { Mutable } from '@libs/services/graphql/utilities/to-mutable';
import { ProgressIndicatorService } from '@libs/services/progress-indicator.service';
import { catchError, firstValueFrom, tap } from 'rxjs';
import { DataEntryStateService } from '../../../../services/data-entry-state.service';
import { StoreStateService } from '../../../../services/store.service';
import { BedTypes } from '../../../../shared/constants/bed-type';
import { DataEntryComponent } from '../../components/data-entry/data-entry.component';
import {
  BedCountChangeEventArgs,
  BedCountTypes,
} from '../../viewmodels/bed-count-change-event-args';
import { IDataEntryRow } from '../../viewmodels/data-entry-row.viewmodel';
import { IDataEntry } from '../../viewmodels/data-entry.viewmodel';

type CapacityReportType = Mutable<GetCapacityReportQuery['capacityReport']>;
@Component({
  selector: 'app-data-entry-page',
  imports: [CommonModule, DataEntryComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './data-entry-page.component.html',
  styleUrl: './data-entry-page.component.scss',
})
export class DataEntryPageComponent implements OnDestroy {
  router = inject(Router);
  getCapacityReportGQL = inject(GetCapacityReportGQL);
  updateCapacityReportGQL = inject(UpdateCapacityReportGQL);
  progressIndicatorService = inject(ProgressIndicatorService);
  dataEntryStateService = inject(DataEntryStateService);
  submitandCompleteCapacityReportGQL = inject(
    SubmitAndCompleteCapacityReportGQL
  );
  store = inject(StoreStateService);

  id = input.required<string>();
  timeoutId: number[] = [];
  nextField = signal<{ row: number; col: number } | undefined>(undefined);

  // Signal-based approach for loading capacity report
  private _capacityReportData = signal<CapacityReportType | null>(null);
  private _isLoading = signal<boolean>(false);
  private _error = signal<Error | null>(null);

  // Effect to automatically reload when id changes - must be at class field level
  private loadEffect = effect(() => {
    const id = this.id();
    if (id) {
      this.loadCapacityReport(id);
    }
  });

  private async loadCapacityReport(id: string): Promise<void> {
    if (!id) return;

    this._isLoading.set(true);
    this._error.set(null);
    this.progressIndicatorService.isProgressIndicator.set(true);
    this.progressIndicatorService.progressIndicatorMessage.set(
      'Loading capacity report...'
    );

    try {
      const result = await firstValueFrom(
        this.getCapacityReportGQL.fetch({ id }, { fetchPolicy: 'network-only' })
      );
      this._capacityReportData.set(
        result.data?.capacityReport as CapacityReportType
      );
    } catch (err) {
      console.error('Error fetching capacity report:', err);
      const error = err instanceof Error ? err : new Error(String(err));
      this._error.set(error);

      if (error && error.message === 'Unauthorized') {
        this.router.navigate(['unauthorized']);
      } else {
        this.router.navigate(['temporarily-unavailable']);
      }
      this._capacityReportData.set(null);
    } finally {
      this._isLoading.set(false);
      this.progressIndicatorService.isProgressIndicator.set(false);
      this.progressIndicatorService.progressIndicatorMessage.set('');
    }
  }

  // Access the resource data
  capacityReportData = computed(() => this._capacityReportData());
  isLoading = computed(() => this._isLoading());
  error = computed(() => this._error());

  // Method to manually reload the data
  public reload(): void {
    const id = this.id();
    if (id) {
      this.loadCapacityReport(id);
    }
  }

  // Computed properties derived from the resource data
  protected dataEntrySignal = computed(() => {
    const report = this.capacityReportData() as CapacityReportType;
    if (!report) return undefined;

    return {
      facilityCity: report.facilityCity,
      facilityName: report.facilityName,
      facilityHasSmokingPolicy: report.facilityHasSmokingPolicy,
      dataEntryRows: this.createDataEntryRows(report),
      datePeriodStart: report.capacityReportPeriodStartDate,
      datePeriodEnd: report.capacityReportPeriodEndDate,
      dateDue: report.capacityReportPeriodDueDate,
      completedOn: report.submittedAt ?? undefined,
      reporter: report.submittedByName ?? undefined,
      status: report.status,
      noChangesFromPreviousReport: report.noChangesFromPreviousReport ?? false,
      reasonsForChange: report.reasonsForChange ?? [],
      hasOtherReasonForChange: report.hasOtherReasonForChange ?? false,
      otherReasonForChange: report.otherReasonForChange ?? '',
      hasSmokingPolicy: report.facilityHasSmokingPolicy,
    } as IDataEntry;
  });

  // Effect to update store when data entry changes
  private updateStoreEffect = effect(() => {
    const dataEntry = this.dataEntrySignal();
    if (dataEntry) {
      this.store.updateDataEntry(dataEntry);
    }
  });

  private createDataEntryRows(report: CapacityReportType): IDataEntryRow[] {
    return [
      {
        bedType: BedTypes[0],
        fundedBeds: report.typeAFundedBedCount,
        operationalBeds:
          report.typeAOperationalBedCount === undefined ||
          report.typeAOperationalBedCount === null
            ? undefined
            : report.typeAOperationalBedCount,
        previousOperationalBeds:
          report.typeAOperationalBedCountPrevious === undefined ||
          report.typeAOperationalBedCountPrevious === null
            ? undefined
            : report.typeAOperationalBedCountPrevious,

        occupiedBeds:
          report.typeAOccupiedBedCount === undefined ||
          report.typeAOccupiedBedCount === null
            ? undefined
            : report.typeAOccupiedBedCount,
        previousOccupiedBeds:
          report.typeAOccupiedBedCountPrevious === undefined ||
          report.typeAOccupiedBedCountPrevious === null
            ? undefined
            : report.typeAOccupiedBedCountPrevious,
      },
      {
        bedType: BedTypes[1],
        fundedBeds: report.typeBFundedBedCount,
        operationalBeds:
          report.typeBOperationalBedCount === undefined ||
          report.typeBOperationalBedCount === null
            ? undefined
            : report.typeBOperationalBedCount,
        previousOperationalBeds:
          report.typeBOperationalBedCountPrevious === undefined ||
          report.typeBOperationalBedCountPrevious === null
            ? undefined
            : report.typeBOperationalBedCountPrevious,

        occupiedBeds:
          report.typeBOccupiedBedCount === undefined ||
          report.typeBOccupiedBedCount === null
            ? undefined
            : report.typeBOccupiedBedCount,
        previousOccupiedBeds:
          report.typeBOccupiedBedCountPrevious === undefined ||
          report.typeBOccupiedBedCountPrevious === null
            ? undefined
            : report.typeBOccupiedBedCountPrevious,
      },
      {
        bedType: BedTypes[2],
        fundedBeds: report.typeBSecureFundedBedCount,
        operationalBeds:
          report.typeBSecureOperationalBedCount === undefined ||
          report.typeBSecureOperationalBedCount === null
            ? undefined
            : report.typeBSecureOperationalBedCount,
        previousOperationalBeds:
          report.typeBSecureOperationalBedCountPrevious === undefined ||
          report.typeBSecureOperationalBedCountPrevious === null
            ? undefined
            : report.typeBSecureOperationalBedCountPrevious,

        occupiedBeds:
          report.typeBSecureOccupiedBedCount === undefined ||
          report.typeBSecureOccupiedBedCount === null
            ? undefined
            : report.typeBSecureOccupiedBedCount,
        previousOccupiedBeds:
          report.typeBSecureOccupiedBedCountPrevious === undefined ||
          report.typeBSecureOccupiedBedCountPrevious === null
            ? undefined
            : report.typeBSecureOccupiedBedCountPrevious,
      },
      {
        bedType: BedTypes[3],
        fundedBeds: report.typeCFundedBedCount,
        operationalBeds:
          report.typeCOperationalBedCount === undefined ||
          report.typeCOperationalBedCount === null
            ? undefined
            : report.typeCOperationalBedCount,
        previousOperationalBeds:
          report.typeCOperationalBedCountPrevious === undefined ||
          report.typeCOperationalBedCountPrevious === null
            ? undefined
            : report.typeCOperationalBedCountPrevious,
        occupiedBeds:
          report.typeCOccupiedBedCount === undefined ||
          report.typeCOccupiedBedCount === null
            ? undefined
            : report.typeCOccupiedBedCount,
        previousOccupiedBeds:
          report.typeCOccupiedBedCountPrevious === undefined ||
          report.typeCOccupiedBedCountPrevious === null
            ? undefined
            : report.typeCOccupiedBedCountPrevious,
      },
    ];
  }

  onDataEntryRowChange(payload: BedCountChangeEventArgs) {
    // Store the next field to focus BEFORE making the save call
    const nextFieldToFocus = payload.nextFieldCoords || undefined;
    this.nextField.set(nextFieldToFocus);

    const key = `type${payload.changedFieldCoords.bedType.type}${
      BedCountTypes[payload.changedFieldCoords.countType]
    }BedCount` as keyof Pick<
      CapacityReportType,
      | 'typeAOperationalBedCount'
      | 'typeAOccupiedBedCount'
      | 'typeBOperationalBedCount'
      | 'typeBOccupiedBedCount'
      | 'typeBSecureOperationalBedCount'
      | 'typeBSecureOccupiedBedCount'
      | 'typeCOperationalBedCount'
      | 'typeCOccupiedBedCount'
    >;

    // Save the change and ensure focus is restored after the reload
    this.save([{ name: key, value: payload.changedFieldValue }]).then(() => {
      // Re-set the focus field after the save operation completes
      // to ensure it's available when components re-render
      setTimeout(() => {
        this.nextField.set(nextFieldToFocus);
      }, 50);
    });
  }
  onReasonsForChange(reasons: string[]) {
    this.save([
      { name: 'reasonsForChange', value: reasons },
      { name: 'hasOtherReasonForChange', value: reasons.includes('Other') },
    ]);
  }

  onOtherReasonChange(value: string) {
    this.save([{ name: 'otherReasonForChange', value: value.trim() }]);
  }

  onSubmitted(context: {
    areReasonsForChangeEnabled: boolean;
    isOtherReasonEnabled: boolean;
    facilityHasSmokingPolicy: boolean;
  }) {
    this.submit(context);
  }

  onSaveLater() {
    this.dataEntryStateService.notifyEditorClosed();
    // Do nothing, because we save on every change
  }

  private save(
    values: {
      name: keyof CapacityReportType;
      value: CapacityReportType[keyof CapacityReportType];
    }[]
  ): Promise<void> {
    const report = this.capacityReportData() as CapacityReportType;
    if (!report?.id) return Promise.resolve();

    const input = Object.fromEntries(
      values.map(({ name, value }) => [name, value])
    ) as UpdateCapacityReportInput;

    return firstValueFrom(
      this.updateCapacityReportGQL
        .mutate({
          id: report.id,
          input,
        })
        .pipe(
          tap((result) => {
            if (result.data?.updateCapacityReport) {
              this.dataEntryStateService.currentCapacityReport.set(
                result.data.updateCapacityReport
              );

              // Reload the resource to get fresh data
              this.reload();
            }
          }),
          catchError((error) => {
            console.error('Error updating capacity report:', error);
            throw error; // Re-throw to maintain Promise rejection behavior
          })
        )
    ).then(() => {
      // Convert to Promise<void> after firstValueFrom
    });
  }
  private async submit(context: {
    areReasonsForChangeEnabled: boolean;
    isOtherReasonEnabled: boolean;
    facilityHasSmokingPolicy: boolean;
  }) {
    const report = this.capacityReportData() as CapacityReportType;
    if (!report?.id) return;

    if (!context.areReasonsForChangeEnabled) {
      await this.save([
        { name: 'reasonsForChange', value: [] },
        { name: 'hasOtherReasonForChange', value: false },
        { name: 'otherReasonForChange', value: '' },
      ]);
    } else if (!context.isOtherReasonEnabled) {
      await this.save([
        { name: 'hasOtherReasonForChange', value: false },
        { name: 'otherReasonForChange', value: '' },
      ]);
    }

    this.progressIndicatorService.isProgressIndicator.set(true);
    this.progressIndicatorService.progressIndicatorMessage.set(
      'Submitting capacity report...'
    );
    const timeoutId = window.setTimeout(() => {
      if (this.progressIndicatorService.isProgressIndicator()) {
        console.error('Request took more time to complete');
        this.router.navigate(['temporarily-unavailable']);
      }
    }, (this.dataEntryStateService.timeoutInterval || 3) * 1000);
    this.timeoutId.push(timeoutId);

    try {
      await firstValueFrom(
        this.submitandCompleteCapacityReportGQL
          .mutate({
            id: report.id,
            facilityHasSmokingPolicy: context.facilityHasSmokingPolicy,
          })
          .pipe(
            tap((result) => {
              if (this.progressIndicatorService.isProgressIndicator()) {
                this.progressIndicatorService.isProgressIndicator.set(false);
                this.progressIndicatorService.progressIndicatorMessage.set('');
              }

              this.dataEntryStateService.submissionResult.set({
                success: true,
                context: { facilityName: report.facilityName },
              });
              if (result.data?.submitAndCompleteCapacityReport) {
                console.log(
                  `And we have the report come back after mutation. Its status is ${result.data.submitAndCompleteCapacityReport.status}`
                );
                // Reload the resource to get fresh data
                this.reload();
              } else {
                console.error(`The report was not returned after mutation.`);
              }
              this.dataEntryStateService.notifyEditorClosed();
            }),
            catchError((error: unknown) => {
              console.error('Error updating capacity report:', error);
              this.progressIndicatorService.isProgressIndicator.set(false);
              this.progressIndicatorService.progressIndicatorMessage.set('');
              this.dataEntryStateService.submissionResult.set({
                success: false,
                error: this.getMessageFrom(error),
                context: { facilityName: report.facilityName },
              });
              this.router.navigate(['temporarily-unavailable']);
              throw error; // Re-throw to maintain error behavior
            })
          )
      );
    } catch {
      // Error handling is already done in the catchError operator above
      // This catch is just to prevent unhandled promise rejection
    }
  }

  private getMessageFrom(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }
    return 'Unknown error occurred';
  }

  ngOnDestroy(): void {
    // Clean up any pending timeouts
    this.timeoutId.forEach((id) => clearTimeout(id));
    this.timeoutId = [];
  }
}
